import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Tooltip,
  Typography,
  Collapse,
  Input,
  Button,
} from "antd";
import {
  RobotOutlined,
  ClockCircleOutlined,
  KeyOutlined,
  DollarOutlined,
  UserOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  ChatMessage,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ToolCall,
} from "../../model_gen/chat";
import { formatCurrency, formatTextOrJson } from "../../utils/FormatUtils";
import ModelInput from "../shared/input/ModelInput";

const { Text } = Typography;
const { Panel } = Collapse;

interface RequestOverviewProps {
  request: ChatCompletionRequest;
  response?: ChatCompletionResponse;
  isEditable?: boolean;
  onMessageEdit?: (index: number, content: string) => void;
  onMessageEditComplete?: (index: number, content: string) => void;
  onModelChange?: (model: string) => void;
  intentGroupId?: number;
  style?: React.CSSProperties;
  editingMessageIndex?: number | null;
  onEditingMessageChange?: (index: number | null) => void;
  editMode?: "all" | "final-assistant";
  showDetails?: boolean;
  expanded?: boolean;
  onToolCallChange?: (
    toolCallIndex: number,
    field: string,
    value: string
  ) => void;
  onAddToolCall?: () => void;
  onConfirmAddToolCall?: () => void;
  showAddToolCall?: boolean;
  newToolCall?: { name: string; arguments: string };
  setNewToolCall?: (toolCall: { name: string; arguments: string }) => void;
  setShowAddToolCall?: (show: boolean) => void;
  onDeleteToolCall?: (toolCallIndex: number) => void;
  annotationLabel?: any;
}

const RequestOverview: React.FC<RequestOverviewProps> = ({
  request,
  response,
  isEditable = false,
  onMessageEdit,
  onMessageEditComplete,
  onModelChange,
  intentGroupId,
  style,
  editingMessageIndex,
  onEditingMessageChange,
  editMode = "all",
  showDetails = true,
  expanded = false,
  onToolCallChange,
  onAddToolCall,
  onConfirmAddToolCall,
  showAddToolCall,
  newToolCall,
  setNewToolCall,
  setShowAddToolCall,
  onDeleteToolCall,
}) => {
  // Use a ref to keep track of the latest edited messages
  const [editedMessages, setEditedMessages] = useState<Record<string, string>>(
    {}
  );

  // Reset edited messages when request changes
  useEffect(() => {
    setEditedMessages({});
  }, [request]);

  const getMessageIcon = (role: string) => {
    switch (role) {
      case "user":
        return <UserOutlined style={{ marginRight: 8, color: "#52c41a" }} />;
      case "assistant":
        return <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />;
      case "system":
        return (
          <InfoCircleOutlined style={{ marginRight: 8, color: "#fa8c16" }} />
        );
      default:
        return null;
    }
  };

  const getMessageStyle = (role: string) => {
    switch (role) {
      case "user":
        return { backgroundColor: "#f6ffed", borderColor: "#b7eb8f" };
      case "assistant":
        return { backgroundColor: "#e6f7ff", borderColor: "#91d5ff" };
      case "system":
        return { backgroundColor: "#fff7e6", borderColor: "#ffd591" };
      default:
        return {};
    }
  };

  const messages = [
    ...(request.params.messages || []),
    ...(response?.choices?.[0]?.message ? [response.choices[0].message] : []),
  ];

  // Get all message indices for defaultActiveKey when expanded
  const defaultActiveKeys = expanded
    ? messages.map((_, index) => index.toString())
    : [messages.length - 1].map(String);

  // Function to determine if a message is editable
  const isMessageEditable = (index: number, message: ChatMessage) => {
    if (!isEditable) return false;

    if (editMode === "final-assistant") {
      return index === messages.length - 1 && message.role === "assistant";
    }

    // In "all" mode, allow editing any message if isEditable is true
    return true;
  };

  const isMessageEdited = (index: number) => {
    // Check if we have local edits for this message using the ref
    if (editedMessages[index.toString()] !== undefined) {
      return true;
    }

    // Otherwise, check for server-side edits
    const isLastMessage = index === request.params.messages.length - 1;
    return (
      isLastMessage &&
      (request.metadata?._maitai_annotation_label !== undefined ||
        request.metadata?._maitai_test_request_response !== undefined ||
        (request.metadata?._maitai_test_request_response_tool_calls &&
          (request.metadata?._maitai_test_request_response_tool_calls as any[])
            .length > 0))
    );
  };

  const getMessageContent = (message: ChatMessage, index: number) => {
    // First check if we have local edits for this message using the ref
    if (editedMessages[index.toString()] !== undefined) {
      return editedMessages[index.toString()];
    }

    // Otherwise, follow the original logic
    const isLastMessage = index === messages.length - 1;
    if (isLastMessage) {
      if (request.metadata?._maitai_annotation_label) {
        const annotationLabel =
          typeof request.metadata._maitai_annotation_label === "string"
            ? JSON.parse(request.metadata._maitai_annotation_label)
            : request.metadata._maitai_annotation_label;
        return annotationLabel.content;
      }
      const testResponse = request.metadata?._maitai_test_request_response as
        | string
        | undefined;
      if (testResponse !== undefined && testResponse !== null) {
        return testResponse;
      }
    }
    return message.content;
  };

  // Handle local message edits
  const handleMessageEdit = (index: number, content: string) => {
    setEditedMessages({
      ...editedMessages,
      [index.toString()]: content,
    });

    // Also call the parent handler if provided
    if (onMessageEdit) {
      onMessageEdit(index, content);
    }
  };

  // Handle when editing is complete
  const handleEditingComplete = (index: number | null) => {
    if (index !== null && editedMessages[index.toString()] !== undefined) {
      // Propagate the final edited content to the parent using the ref value
      if (onMessageEditComplete) {
        onMessageEditComplete(index, editedMessages[index.toString()]);
      }
    }

    // Notify parent that editing has ended
    if (onEditingMessageChange) {
      onEditingMessageChange(null);
    }
  };

  const renderModelSelect = (value: string) => {
    return (
      <ModelInput
        value={value}
        onChange={onModelChange}
        placeholder="Select a model"
        label=""
        showEmptyOption={false}
        intentGroupId={intentGroupId}
      />
    );
  };

  const renderToolCalls = (message: ChatMessage, index: number) => {
    if (!isMessageEditable(index, message)) {
      if (!message.tool_calls?.length) return null;

      return (
        <div style={{ marginTop: "8px" }}>
          <strong style={{ fontSize: "0.85em" }}>Tool Calls:</strong>
          {message.tool_calls.map((toolCall: ToolCall, i: number) => (
            <div
              key={toolCall.id || i}
              style={{
                borderBottom: "1px solid #d9d9d9",
                padding: "4px 0",
                marginBottom: "4px",
              }}>
              <div>
                <strong style={{ fontSize: "0.85em" }}>Function:</strong>{" "}
                {toolCall.function.name}
              </div>
              <div>
                <strong style={{ fontSize: "0.85em" }}>Arguments:</strong>
                <pre
                  style={{
                    margin: "2px 0",
                    padding: "4px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "2px",
                    fontSize: "0.85em",
                  }}>
                  {toolCall.function.arguments}
                </pre>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "4px",
          }}>
          <strong>Tool Calls</strong>
          <Button type="primary" size="small" onClick={onAddToolCall}>
            Add Tool Call
          </Button>
        </div>

        {message.tool_calls?.map((toolCall: ToolCall, toolIndex: number) => (
          <div
            key={toolCall.id || toolIndex}
            style={{
              borderBottom: "1px solid #d9d9d9",
              padding: "4px 0",
              marginBottom: "4px",
            }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <strong style={{ fontSize: "0.85em" }}>Function:</strong>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => onDeleteToolCall?.(toolIndex)}
              />
            </div>
            <Input
              value={toolCall.function.name}
              onChange={(e) =>
                onToolCallChange?.(toolIndex, "name", e.target.value)
              }
              style={{
                width: "100%",
                maxWidth: "calc(100% - 32px)",
                padding: "2px 4px",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                marginTop: "1px",
                fontSize: "0.85em",
                height: "24px",
              }}
            />
            <div>
              <strong style={{ fontSize: "0.85em" }}>Arguments:</strong>
              <Input.TextArea
                value={toolCall.function.arguments}
                onChange={(e) =>
                  onToolCallChange?.(toolIndex, "arguments", e.target.value)
                }
                style={{
                  width: "100%",
                  maxWidth: "calc(100% - 32px)",
                  padding: "2px 4px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "2px",
                  resize: "vertical",
                  maxHeight: "100px",
                  marginTop: "1px",
                  fontSize: "0.85em",
                  lineHeight: "1.2",
                }}
              />
            </div>
          </div>
        ))}

        {showAddToolCall && (
          <div
            style={{
              borderBottom: "1px solid #1890ff",
              padding: "4px 0",
              marginBottom: "4px",
            }}>
            <div style={{ marginBottom: "2px" }}>
              <strong style={{ fontSize: "0.85em" }}>Function:</strong>
              <Input
                value={newToolCall?.name}
                onChange={(e) =>
                  setNewToolCall?.({ ...newToolCall!, name: e.target.value })
                }
                style={{
                  width: "100%",
                  maxWidth: "calc(100% - 32px)",
                  padding: "2px 4px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "2px",
                  marginTop: "1px",
                  fontSize: "0.85em",
                  height: "24px",
                }}
              />
            </div>
            <div style={{ marginBottom: "2px" }}>
              <strong style={{ fontSize: "0.85em" }}>Arguments:</strong>
              <Input.TextArea
                value={newToolCall?.arguments}
                onChange={(e) =>
                  setNewToolCall?.({
                    ...newToolCall!,
                    arguments: e.target.value,
                  })
                }
                style={{
                  width: "100%",
                  maxWidth: "calc(100% - 32px)",
                  padding: "2px 4px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "2px",
                  resize: "vertical",
                  minHeight: "40px",
                  maxHeight: "100px",
                  marginTop: "1px",
                  fontSize: "0.85em",
                  lineHeight: "1.2",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "4px",
                marginTop: "2px",
              }}>
              <Button size="small" onClick={() => setShowAddToolCall?.(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={onConfirmAddToolCall}
                disabled={!newToolCall?.name.trim()}>
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card
      size="small"
      style={{
        background: "#f8f9fa",
        borderRadius: "8px",
        marginBottom: "24px",
        ...style,
      }}>
      {showDetails && (
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Tooltip title="Model used for this request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <RobotOutlined
                  style={{
                    fontSize: "24px",
                    color: "#1890ff",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Model
                </Text>
                {isEditable ? (
                  renderModelSelect(request.params.model)
                ) : (
                  <Text strong style={{ fontSize: "16px" }}>
                    {request.params.model}
                  </Text>
                )}
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Time taken to process the request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <ClockCircleOutlined
                  style={{
                    fontSize: "24px",
                    color: "#52c41a",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Time
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {response?.response_time && response.response_time > 0
                    ? `${response.response_time}ms`
                    : "N/A"}
                </Text>
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Number of tokens used">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <KeyOutlined
                  style={{
                    fontSize: "24px",
                    color: "#faad14",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Tokens
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {`${response?.usage?.prompt_tokens || 0} > ${
                    response?.usage?.completion_tokens || 0
                  }`}
                </Text>
              </Card>
            </Tooltip>
          </Col>
          <Col span={6}>
            <Tooltip title="Cost of the request">
              <Card
                size="small"
                style={{ textAlign: "center", height: "100%" }}>
                <DollarOutlined
                  style={{
                    fontSize: "24px",
                    color: "#eb2f96",
                    marginBottom: "8px",
                  }}
                />
                <Text type="secondary" style={{ display: "block" }}>
                  Cost
                </Text>
                <Text strong style={{ fontSize: "16px" }}>
                  {response?.usage?.cost && response.usage.cost > 0
                    ? formatCurrency(response.usage.cost)
                    : "N/A"}
                </Text>
              </Card>
            </Tooltip>
          </Col>
        </Row>
      )}

      <Collapse
        style={{
          marginTop: showDetails ? 20 : 0,
          backgroundColor: "transparent",
        }}
        bordered={false}
        defaultActiveKey={defaultActiveKeys}
        expandIconPosition="end">
        {messages.map((message: ChatMessage, index) => (
          <Panel
            key={index}
            header={
              <div style={{ display: "flex", alignItems: "center" }}>
                {getMessageIcon(message.role)}
                <Text
                  style={{
                    color: "#333",
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}>
                  {message.role}
                  {isMessageEdited(index) && "*"}
                </Text>
              </div>
            }
            style={{
              marginBottom: 16,
              borderRadius: 8,
              border: "1px solid",
              ...getMessageStyle(message.role),
            }}>
            {editingMessageIndex === index &&
            isMessageEditable(index, message) ? (
              <>
                <Input.TextArea
                  value={getMessageContent(message, index)}
                  onChange={(e) => handleMessageEdit(index, e.target.value)}
                  onBlur={() => handleEditingComplete(index)}
                  autoFocus
                  autoSize={{ minRows: 2 }}
                />
                {renderToolCalls(message, index)}
              </>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 14,
                    whiteSpace: "pre-wrap",
                    padding: "8px 12px",
                    cursor: isMessageEditable(index, message)
                      ? "pointer"
                      : "default",
                  }}
                  onClick={() =>
                    isMessageEditable(index, message) &&
                    onEditingMessageChange?.(index)
                  }>
                  {formatTextOrJson(getMessageContent(message, index))}
                </div>
                {renderToolCalls(message, index)}
              </>
            )}
          </Panel>
        ))}
      </Collapse>
    </Card>
  );
};

export default RequestOverview;
